import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, RefreshCw } from "lucide-react";
import CommentsPageShell from "@/components/comments/CommentsPageShell";
import NativeCommentCard from "@/components/social-proof/NativeCommentCard";
import { Button } from "@/components/ui/button";
import {
  api,
  type SocialProofFacets,
  type SocialProofFreshness,
  type SocialProofItem,
} from "@/lib/api";
import { cn } from "@/lib/utils";

const STATUS_OPTS = [
  { id: "", label: "Wszystkie statusy" },
  { id: "candidate", label: "Kandydat" },
  { id: "approved", label: "Zatwierdzony" },
  { id: "needs_consent", label: "Potrzeba zgody" },
  { id: "rejected", label: "Odrzucony" },
  { id: "used", label: "Użyty" },
];

export default function SocialProofPage() {
  const [items, setItems] = useState<SocialProofItem[]>([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState<SocialProofFacets | null>(null);
  const [freshness, setFreshness] = useState<SocialProofFreshness | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [proofType, setProofType] = useState("");
  const [status, setStatus] = useState("candidate");
  const [sourceKind, setSourceKind] = useState("");
  const [mode, setMode] = useState<"native" | "landing">("native");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, fac, fresh] = await Promise.all([
        api.listSocialProof({
          q: q || undefined,
          proof_type: proofType || undefined,
          status: status || undefined,
          source_kind: sourceKind || undefined,
          limit: 80,
        }),
        api.socialProofFacets(),
        api.socialProofFreshness(),
      ]);
      setItems(list.items || []);
      setTotal(list.total || 0);
      setFacets(fac);
      setFreshness(fresh);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Błąd ładowania");
    } finally {
      setLoading(false);
    }
  }, [q, proofType, status, sourceKind]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void load();
    }, 200);
    return () => window.clearTimeout(t);
  }, [load]);

  const patch = async (id: string, body: Parameters<typeof api.patchSocialProof>[1]) => {
    await api.patchSocialProof(id, body);
    await load();
  };

  const onRescan = async () => {
    setBusy(true);
    try {
      await api.socialProofRescan();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Rescan failed");
    } finally {
      setBusy(false);
    }
  };

  const onExport = async () => {
    const data = await api.socialProofExportApproved();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `social-proof-approved-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const igAlert = freshness?.inbox?.ig_dm_alert;
  const counts = facets?.counts || freshness?.bank_meta?.counts;

  const typeChips = useMemo(
    () => facets?.proof_types || [],
    [facets],
  );

  return (
    <CommentsPageShell
      tab="social-proof"
      total={total}
      totalWord="kandydatów"
      q={q}
      onQChange={setQ}
      showSearch
      actions={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void onRescan()}>
            <RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", busy && "animate-spin")} />
            Rescan
          </Button>
          <Button variant="outline" size="sm" onClick={() => void onExport()}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Eksport approved
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {(igAlert || freshness) && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span>
                FB DM: {freshness?.inbox?.messenger?.threads ?? "?"} wątków
                {freshness?.inbox?.messenger?.fetched_at
                  ? ` · ${String(freshness.inbox.messenger.fetched_at).slice(0, 16)}`
                  : ""}
              </span>
              <span>
                IG DM: {freshness?.inbox?.instagram?.threads ?? "?"} wątków
                {freshness?.inbox?.instagram?.fetched_at
                  ? ` · ${String(freshness.inbox.instagram.fetched_at).slice(0, 16)}`
                  : ""}
              </span>
              {freshness?.bank_meta?.last_rescan_at && (
                <span>Ostatni rescan: {String(freshness.bank_meta.last_rescan_at).slice(0, 16)}</span>
              )}
            </div>
            {igAlert && <p className="mt-1 text-amber-700">{igAlert}</p>}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
            <button
              type="button"
              className={cn(
                "rounded-md px-3 py-1 text-xs font-medium",
                mode === "native" ? "bg-white shadow-sm text-slate-900" : "text-slate-500",
              )}
              onClick={() => setMode("native")}
            >
              Native (jak w app)
            </button>
            <button
              type="button"
              className={cn(
                "rounded-md px-3 py-1 text-xs font-medium",
                mode === "landing" ? "bg-white shadow-sm text-slate-900" : "text-slate-500",
              )}
              onClick={() => setMode("landing")}
            >
              Cytat landing
            </button>
          </div>
          <select
            className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTS.map((o) => (
              <option key={o.id || "all"} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs"
            value={sourceKind}
            onChange={(e) => setSourceKind(e.target.value)}
          >
            <option value="">Wszystkie źródła</option>
            {(facets?.source_kinds || ["comment_fb", "comment_ig", "dm_fb", "dm_ig"]).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {typeChips.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setProofType((prev) => (prev === t.id ? "" : t.id))}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs font-medium",
                proofType === t.id
                  ? "border-brand/40 bg-brand/10 text-brand"
                  : "border-slate-200 bg-white text-slate-600",
              )}
            >
              {t.label}
              {counts?.by_type?.[t.id] != null ? ` (${counts.by_type[t.id]})` : ""}
            </button>
          ))}
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            Ładowanie kandydatów…
          </div>
        ) : !items.length ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            Brak kandydatów. Kliknij Rescan albo zbierz inbox (Messenger / IG DM).
          </div>
        ) : (
          <div className="mx-auto grid max-w-[420px] gap-5">
            {items.map((item) => (
              <div key={item.id} className="space-y-2">
                <div className="overflow-hidden rounded-[28px] border border-slate-300 bg-slate-100 p-2 shadow-md">
                  <div className="mb-1 flex items-center justify-between px-2 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                    <span>{item.source_kind}</span>
                    <span>
                      {item.proof_label} · {item.score}
                    </span>
                  </div>
                  <NativeCommentCard item={item} mode={mode} />
                </div>
                <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
                  <Button
                    size="sm"
                    variant={item.status === "approved" ? "default" : "outline"}
                    onClick={() => void patch(item.id, { status: "approved" })}
                  >
                    Zatwierdź
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void patch(item.id, { status: "needs_consent" })}
                  >
                    Zgoda
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void patch(item.id, { status: "rejected" })}
                  >
                    Odrzuć
                  </Button>
                  <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={Boolean(item.suitable_for_screenshot)}
                      onChange={(e) =>
                        void patch(item.id, { suitable_for_screenshot: e.target.checked })
                      }
                    />
                    Na screen
                  </label>
                  <span className="text-[11px] text-slate-400">{item.status}</span>
                </div>
                <textarea
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700"
                  rows={2}
                  defaultValue={item.quote_anon || item.text}
                  key={`${item.id}-quote-${item.status}`}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== (item.quote_anon || item.text)) {
                      void patch(item.id, { quote_anon: v });
                    }
                  }}
                  placeholder="Wersja anonimowa pod landing…"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </CommentsPageShell>
  );
}
